import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { NoteColor, NoteType } from "../constants";
import {
    getCurrentTemplate,
    getNotes,
    getPresentationId,
    getProcessStep,
    getUndoAction, Note,
    State
} from "../reducers";
import * as store from "../services/presentation-store";
import { toCanvasX, toCanvasY, toStoryboardX, toStoryboardY } from "../services/template-service";
import { Undoable } from "./undo";
import { reportError, ReportErrorAction } from "./ui-state";

export interface StartNoteDraggingAction {
    readonly type: 'START_NOTE_DRAGGING',
    readonly uuid: string,
    readonly offsetX: number,
    readonly offsetY: number,
    readonly noteX: number,
    readonly noteY: number,
}

export interface NoteDraggingAction extends Undoable {
    readonly type: 'NOTE_DRAGGING',
    readonly uuid: string,
    readonly endX: number,
    readonly endY: number,
}

export interface StopNoteDraggingAction extends Undoable {
    readonly type: 'STOP_NOTE_DRAGGING',
    readonly uuid: string,
    readonly endX: number,
    readonly endY: number,
}

export interface MoveNotesAction extends Undoable {
    readonly type: 'MOVE_NOTE',
    readonly uuid: string,
    readonly startX: number,
    readonly startY: number,
    readonly endX: number,
    readonly endY: number,
}

export interface DeleteNoteAction extends Undoable {
    readonly type: 'DELETE_NOTE',
    readonly uuid: string,
    readonly text: string,
    readonly color: NoteColor,
    readonly noteType: NoteType;
    readonly noteX: number,
    readonly noteY: number,
}


export function startNoteDragging(uuid: string, mouseX: number, mouseY: number): ThunkAction<any, State, any, StartNoteDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StartNoteDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const notes = getNotes(state);
        const note = notes.find(currentNote => currentNote.uuid === uuid);
        if (note) {
            const offsetX = note.x - mouseX;
            const offsetY = note.y - mouseY;
            doStartNoteDragging(dispatch, state, note, offsetX, offsetY);
        }
    };
}

export function startNoteDraggingOnStoryboard(uuid: string, mouseX: number, mouseY: number): ThunkAction<any, State, any, StartNoteDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StartNoteDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const template = getCurrentTemplate(state);
        const notes = getNotes(state);
        const note = notes.find(currentNote => currentNote.uuid === uuid);
        if (note) {
            const offsetX = toStoryboardX(template, note.x) - mouseX;
            const offsetY = toStoryboardY(template, note.y) - mouseY;
            doStartNoteDragging(dispatch, state, note, offsetX, offsetY);
        }
    }
}

function doStartNoteDragging(dispatch: ThunkDispatch<State, any, StartNoteDraggingAction>, state: State, note: Note, offsetX: number, offsetY: number) {
    dispatch({
        type: 'START_NOTE_DRAGGING',
        uuid: note.uuid,
        processStep: getProcessStep(state),
        offsetX,
        offsetY,
        noteX: note.x,
        noteY: note.y,
    })
}


export function noteDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, NoteDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, NoteDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        await doNoteDragging(dispatch, state, mouseX, mouseY);
    }

}
export function noteDraggingOnStoryboard(mouseX: number, mouseY: number): ThunkAction<any, State, any, NoteDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, NoteDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const template = getCurrentTemplate(state);
        const x = toCanvasX(template, mouseX);
        const y = toCanvasY(template, mouseY);
        await doNoteDragging(dispatch, state, x, y);
    }
}

async function doNoteDragging(dispatch: ThunkDispatch<State, any, NoteDraggingAction | ReportErrorAction>, state: State, mouseX: number, mouseY: number) {
    const previousAction = getUndoAction(state);
    if (previousAction && previousAction.type === 'MOVE_NOTE') {
        const presentationId = getPresentationId(state);
        const {uuid, startX, startY} = previousAction;
        const redoEffect = async () => await store.moveNote(presentationId, uuid, mouseX, mouseY)
            .catch(error => dispatch(reportError(error)));
        const undoEffect = async () => await store.moveNote(presentationId, uuid, startX, startY)
            .catch(error => dispatch(reportError(error)));
        dispatch({
            type: 'NOTE_DRAGGING',
            uuid,
            endX: mouseX,
            endY: mouseY,
            redoEffect,
            undoEffect,
        });
        await redoEffect();
    }
}


export function stopNoteDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopNoteDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StopNoteDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        await doStopNoteDragging(dispatch, state, mouseX, mouseY)
    }
}

export function stopNoteDraggingOnStoryboard(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopNoteDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StopNoteDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const template = getCurrentTemplate(state);
        const x = toCanvasX(template, mouseX);
        const y = toCanvasY(template, mouseY);
        await doStopNoteDragging(dispatch, state, x, y);
    }
}

export async function doStopNoteDragging(dispatch: ThunkDispatch<State, any, StopNoteDraggingAction | ReportErrorAction>, state: State, mouseX: number, mouseY: number) {
    const previousAction = getUndoAction(state);
    if (previousAction && previousAction.type === 'MOVE_NOTE') {
        const presentationId = getPresentationId(state);
        const {uuid, startX, startY} = previousAction;
        const redoEffect = async () => await store.moveNote(presentationId, uuid, mouseX, mouseY)
            .catch(error => dispatch(reportError(error)));
        const undoEffect = async () => await store.moveNote(presentationId, uuid, startX, startY)
            .catch(error => dispatch(reportError(error)));
        dispatch({
            type: 'STOP_NOTE_DRAGGING',
            uuid,
            endX: mouseX,
            endY: mouseY,
            redoEffect,
            undoEffect,
        });
        await redoEffect();
    }
}


export function deleteNote(uuid: string): ThunkAction<any, State, any, DeleteNoteAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, DeleteNoteAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const presentationId = getPresentationId(state);
        const notes = getNotes(state);
        const note = notes.find(currentNote => currentNote.uuid === uuid);
        if (note) {
            const redoEffect = async () => await store.deleteNote(presentationId, uuid)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.createNote(presentationId, uuid, note.x, note.y, note.text, note.color)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'DELETE_NOTE',
                uuid,
                text: note.text,
                color: note.color,
                noteType: note.type,
                noteX: note.x,
                noteY: note.y,
                undoEffect,
                redoEffect,
            });
            await redoEffect();
        }
    }
}



// WEBPACK FOOTER //
// ./src/actions/notes.ts